<template>
  <config-base show-plan-details class="opc-zclips" @view-details="handleViewDetails" :config-plan-tile-status="configPlanTileStatus">
    <div v-if="alertMsg" slot="topAlert">
      <alert class="opc-zclips-top__alert" type="info">
          <span class="opc-zclips-top__alert-msg" v-html="alertMsg" />
      </alert>
    </div>
    <div slot="config" class="opc-zclips__config">
      <p class="opc-zclips__input-desc">
        {{ $t('billing.zone.select_license') }}
      </p>
      <input-number-with-popover class="opc-zclips__quantity-selector" v-model="selectedNumber" size="large"
        controls-position="both" :min="minHosts" :max="maxHosts" @change="changeLicense" :decrease-tips="decreaseTips"
        :label="trackingPlanName + ' Select the number of users'" />
        <license-tip ref="licenseTip" class="mgb-sm" type="zclips" :number="selectedNumber" :additional-spans="additionalSpans">
        </license-tip>
        <alert v-if="mx_downSellLicenseErrorMessage" class="additional-template__alert" type="info">
          <span v-html="mx_downSellLicenseErrorMessage" />
        </alert>
      <config-cycle v-model="billingCycle" :billing-cycle-list="billingCycleList" :showMonthlyPriceFromCycle="true"
        @switchCycleAction="switchCycleAction" :show-cycle-alert="showCycleAlert" :unit="0" />
      <div v-if="showSwitchToMeeting" class="opc-zclips__switch-notice">
        <span>
          {{$t('billing.custom_avatars.switch_to_meeting_notice', [getFreeUserMeetingMinutes()])}}
        </span>
        <zm-button @click="handleNavigateBack" type="link">{{$t('billing.custom_avatars.switch_to_meeting_btn')}}</zm-button>
      </div>
      <preview-payment class="opc-zclips__preview-payment" />
    </div>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-124" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + trackingPlanName + ' plan'">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import { mapActions, mapGetters, mapMutations } from 'vuex';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/components/config-base';
import commonMixin from '@/plans/ZOOM_CLIPS/mixin';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { planTypes, trackingPlanTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import LicenseTip from '@/plans/ZOOM_MEETING/license-tip.vue';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import AddonMixin from '@/plans/mx/addon';
import _ from "lodash"
import mxPlanDetails from '../dialog/mx-plan-details';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import { getFreeUserMeetingMinutes } from '../../../utils/meeting';

const ConfigBaseComp = {
  name: 'ZoomClipsBaseComp',
  extends: ConfigBase,
  mixins: [commonMixin],
  key: planTypes[planTypes.ZOOM_CLIPS],
  props: {
    showPlanDetails: Boolean
  },
  data() {
    return {
      showLearnMore: false,
    };
  },
};

export default {
  name: "ZoomClipsPlusBase",
  mixins: [plansMixin, commonMixin, AddonMixin, mxPlanDetails],
  components: {
    PreviewPayment,
    ConfigCycle,
    InputNumberWithPopover,
    ConfigBase: ConfigBaseComp,
    LicenseTip,
    Alert
  },
  data() {
    return {
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.ZOOM_CLIPS,
      showCycleAlert: true,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlansMap', 'getSelectedPlans', 'planMapFlat', 'getUserCurrentSubscriptions', 'getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans', 'boughtPlans']),
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    selectedPlan() {
      return this.getTypeGroupSelectedPlans[this.planType]?.[0];
    },
    minHosts() {
      return 1;
    },
    maxHosts() {
      return 99;
    },
    decreaseTips() {
      return '';
    },
    disabledForBulkLicense() {
      return () => false;
    },
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },
    canRmv() {
      return false;
    },
    additionalSpans() {
      return {
        zclips: [
          {
            value: 1,
            text: '',
          },
          {
            value: 99,
            text: this.$t('billing.zone.max_of_plans', [99, 100]),
          },
        ]
      }
    },
    alertMsg() {
      return "";
    },
    showSwitchToMeeting () {
      return false;
    },
    depCurrentAllAddonCyclePlanList() {
      return this.getUserCurrentSubscriptions?.filter(p => p?.billingCycleDependBasePlanTypeList?.includes(this.planType)) ?? [];
    },

    depAddonMaxCyclePlan() {
      return  _.chain(this.depCurrentAllAddonCyclePlanList)
          .orderBy((p) => p.cycleValue, 'desc')
          .get(0)
          .value();
    },
  },
  methods: {
    getFreeUserMeetingMinutes,
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    changeLicense(newLicense, oldLicense) {
      this.selectedNumber = newLicense;
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense)

      this.$tracking.buyInteraction(
        `number of licenses`,
        `${this.trackingPlanName} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`,
      );

      if (newLicense === this.maxHosts) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - impression`,
        );
      }
    },
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.selectedNumber <= this.maxHosts ? this.selectedNumber : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.addOnBuyInteraction(
        `${this.trackingPlanName} billing cycle`,
        this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    },
    async handleViewDetails() {
      await this.showPlanDetailDialog();
    },

    handleNavigateBack () {
      window.history.back();
    },
  },
  created() {
    const selectedPlan = this.selectedPlan;

    if (selectedPlan) {
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.selectedNumber);
      });
    }
  },
  mounted() {
    this.$refs?.licenseTip?.$el?.addEventListener('click', (e) => {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - contact sales`,
        );
      }
    });
  }
};
</script>

<style lang="scss">
.opc-zclips {
  &__input-desc {
    color: #00031F;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    margin-bottom: 8px;
  }

  &__quantity-selector {
    margin-bottom: 10px;
  }

  .opc-zclips-top__alert {
    display: flex;
    padding: 16px;
    align-items: center;
    gap: 16px;
    border-radius: 8px;
    background: #F7F7F8;
    margin-bottom: 24px;

    .zm-alert__icon {
      margin-right: 0;
    }

    .zm-alert__body {
      padding: 0;
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
      line-height: 150%;
      strong {
        font-weight: bold;
        color: #00053D;
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

  &__switch-notice {
    margin-top: 24px;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 400;
    color: #666487;
    .zm-button--link {
      padding: 0;
    }
  }
}
</style>
